/**
 * Announcement banner — the quiet presentation, for smaller news.
 *
 * Sits under the Header in document flow. Purely presentational: the
 * host decides whether there is anything to show and owns the
 * acknowledgement, so this cannot disagree with the modal about which
 * announcement is current.
 *
 *   - Dismissing writes to the server, so it stays dismissed on their
 *     phone too — not just in the browser they happened to be using.
 *   - Opening the action dismisses it. Nobody wants to read the thing
 *     and then still be told about it.
 */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Box, Container, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import type { AckVia } from '../../api/announcements';
import type { Announcement } from '../../api/announcements';
import { colors, docLeading, uiType } from '../../theme';

const GOLD = colors.goldDark;

export function AnnouncementBanner({
  announcement,
  onDismiss,
  index = 1,
  total = 1,
  onNext,
  onBack,
  canGoBack,
}: {
  announcement: Announcement;
  onDismiss: (via: AckVia) => void;
  /** 1-based position in the run. */
  index?: number;
  /** How many are unseen. 1 hides the counter. */
  total?: number;
  /** Acknowledge this one and move to the next, or close if it is last. */
  onNext?: () => void;
  /** Step back to re-read the previous one. Acknowledges nothing. */
  onBack?: () => void;
  /** False on the first page. */
  canGoBack?: boolean;
}) {
  const navigate = useNavigate();
  // Both handlers or neither: a Back that cannot go back is worse than
  // no Back at all. The admin preview renders this component with no
  // paging wired, which is exactly the case this guards.
  const many = total > 1 && !!onNext && !!onBack;

  return (
    <Box
      role="status"
      sx={{
        borderBottom: `1px solid ${GOLD}33`,
        background: `linear-gradient(to right, ${GOLD}14, ${GOLD}08)`,
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 1400 }}>
        <Box
          sx={{
            display: 'flex',
            // Wraps to two rows on a phone rather than squeezing the
            // text to a column two words wide.
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
            py: 1.25,
          }}
        >
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: GOLD, flexShrink: 0 }} />

          {/* On a phone the text claims the whole row, which is what
              actually triggers the wrap — with `minWidth: 0` it would
              shrink forever instead, squeezing the sentence into a
              three-line column beside the button. */}
          <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
            <Typography
              component="span"
              sx={{
                fontSize: uiType.body,
                fontWeight: 700,
                color: colors.textPrimary,
                mr: 0.75,
              }}
            >
              {announcement.title}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: uiType.body,
                color: colors.textSecondary,
                lineHeight: docLeading.normal,
              }}
            >
              {announcement.body}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5 },
                ml: { xs: 'auto', sm: 0 },
                flexShrink: 0,
              }}
            >
              {announcement.action && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    // Opening it counts as seeing it — nobody wants to read
                    // the thing and then still be told about it.
                    onDismiss('opened');
                    navigate(announcement.action!.to);
                  }}
                  sx={{
                    px: 1.5,
                    py: 0.6,
                    border: `1px solid ${GOLD}59`,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.6)',
                    color: GOLD,
                    font: 'inherit',
                    fontSize: uiType.body,
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.15s, border-color 0.15s',
                    '&:hover': { bgcolor: '#fff', borderColor: GOLD },
                  }}
                >
                  {announcement.action.label}
                </Box>
              )}

              {/* Several unseen announcements are one interruption, paged,
                rather than several banners in a row. The counter is the
                only thing that makes "Next" honest — without it the
                reader cannot tell whether they are agreeing to one more
                or five. */}
              {/* Back sits before Next and is disabled rather than hidden
                  on the first page, so the controls do not shift under
                  the reader as they move through the run. */}
              {many && (
                <Box
                  component="button"
                  type="button"
                  onClick={onBack}
                  disabled={!canGoBack}
                  sx={{
                    px: 1.25,
                    py: 0.6,
                    border: 'none',
                    borderRadius: 1.5,
                    bgcolor: 'transparent',
                    color: colors.textMuted,
                    opacity: canGoBack ? 1 : 0.35,
                    font: 'inherit',
                    fontSize: uiType.body,
                    fontWeight: 600,
                    cursor: canGoBack ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'color 0.15s, opacity 0.15s',
                    '&:hover': canGoBack ? { color: colors.textPrimary } : {},
                  }}
                >
                  Back
                </Box>
              )}

              {many && (
                <Box
                  component="button"
                  type="button"
                  onClick={onNext}
                  sx={{
                    px: 1.5,
                    py: 0.6,
                    border: 'none',
                    borderRadius: 1.5,
                    bgcolor: GOLD,
                    color: '#fff',
                    font: 'inherit',
                    fontSize: uiType.body,
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'filter 0.15s',
                    '&:hover': { filter: 'brightness(1.06)' },
                  }}
                >
                  Next
                  <Box
                    component="span"
                    sx={{ ml: 0.75, opacity: 0.75, fontWeight: 600, fontSize: uiType.caption }}
                  >
                    {index} / {total}
                  </Box>
                </Box>
              )}

              <Box
                component="button"
                type="button"
                aria-label="Dismiss announcement"
                onClick={() => onDismiss('dismissed')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 30,
                  height: 30,
                  p: 0,
                  border: 'none',
                  borderRadius: '50%',
                  bgcolor: 'transparent',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: `${GOLD}1a`, color: GOLD },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 17 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
